import React from 'react';
import { ParsedLine } from '@/lib/leadsheetParser';
import { getCharacterPosition } from '@/lib/textMeasure';
import { transposeChord } from '../utils/chordUtils';

interface LineRendererProps {
  line: ParsedLine;
  lineIndex: number;
  parsedLines: ParsedLine[];
  transpose: number;
  fontSize: string;
  chordFontSize: number;
  chordColor: string;
}

export const LineRenderer: React.FC<LineRendererProps> = ({
  line,
  lineIndex,
  parsedLines,
  transpose,
  fontSize,
  chordFontSize,
  chordColor,
}) => {
  const fontFamily = "'Courier New', monospace";
  const fontSizeNum = parseInt(fontSize);

  // Section headers (Intro, Verse, etc.)
  if (line.isSection) {
    const nextLine = lineIndex + 1 < parsedLines.length ? parsedLines[lineIndex + 1] : null;
    const hasNextChords = nextLine && nextLine.chords.length > 0;
    
    return (
      <div 
        key={lineIndex} 
        className="first:mt-0"
        style={{ 
          marginTop: lineIndex > 0 ? '1.2em' : '0',
          marginBottom: hasNextChords ? '1.2em' : '0.5em'
        }}
      >
        <span className="font-semibold text-muted-foreground">{line.text}</span>
      </div>
    );
  }

  // Lines without chords
  if (line.chords.length === 0) {
    return (
      <div key={lineIndex} style={{ lineHeight: '2.5em' }}>
        {line.text || '\u00A0'}
      </div>
    );
  }

  // Lines with chords - use precise positioning
  return (
    <div key={lineIndex} className="relative" style={{ marginBottom: '0.5em', lineHeight: '2.5em' }}>
      {/* Chords layer */}
      <div className="absolute top-0 left-0 w-full" style={{ transform: 'translateY(-1.5em)' }}>
        {line.chords.map((chord, index) => {
          const transposedChord = transposeChord(chord.chord, transpose);
          
          const measurementString = ' '.repeat(chord.position);
          const pixelPosition = getCharacterPosition(
            measurementString,
            measurementString.length,
            fontSizeNum,
            fontFamily
          );

          return (
            <span
              key={index}
              className="absolute font-bold whitespace-nowrap"
              style={{
                left: `${pixelPosition}px`,
                color: chordColor,
                fontSize: `${chordFontSize}px`
              }}
            >
              {transposedChord}
            </span>
          );
        })}
      </div>

      {/* Text layer - preserve leading spaces */}
      <div style={{ whiteSpace: 'pre' }}>{line.text || '\u00A0'}</div>
    </div>
  );
};

